<template>
    <v-container fluid fill-height>
        <Headful title="Datums doorgeven"></Headful>
        <Header color="white" show-logo :dark="false" extended></Header>
        <v-layout align-center justify-center>
            <v-flex>
                <p v-if="loading" class="text-center my-7">
                    <v-icon large color="primary">fal fa-spinner-third fa-spin</v-icon>
                </p>
                <v-alert v-else-if="notFound" dark color="error" class="text-center my-7">
                    Deze link is niet (meer) geldig.
                </v-alert>
                <div v-else class="px-5">
                    <v-card class="elevation-0 mx-auto" max-width="640">
                        <v-card-text class="pa-7">
                            <h1 class="text-h5 mb-5 text-center">Datums doorgeven</h1>

                            <v-simple-table class="mb-5">
                                <tbody>
                                    <tr><td><strong>Aanvrager</strong></td><td>{{ request.name }}</td></tr>
                                    <tr><td><strong>E-mail</strong></td><td>{{ request.email }}</td></tr>
                                    <tr><td><strong>Schoolbestuur</strong></td><td>{{ request.schoolbestuurName }}</td></tr>
                                    <tr><td><strong>Dienst</strong></td><td>{{ request.organisationName }}</td></tr>
                                    <tr><td><strong>Type dienst</strong></td><td>{{ request.typeDienst }}</td></tr>
                                </tbody>
                            </v-simple-table>

                            <v-alert v-if="success" color="success" class="mb-4">
                                {{ successMessage }}
                            </v-alert>
                            <v-form v-if="canSubmit" ref="form" @submit.prevent="submit" :disabled="submitting">
                                <template v-if="isFirstSubmit">
                                    <p class="mb-4">
                                        Geef de verwachte start- en einddatum van de activiteit door. Staat de einddatum
                                        nog niet vast? Laat dat veld dan leeg; je kunt de einddatum later via dezelfde
                                        link invullen of aanpassen.
                                    </p>
                                    <v-text-field outlined rounded required hide-details="auto" class="mb-4"
                                        type="date" label="Verwachte startdatum"
                                        v-model="expectedStartDate" :rules="[requiredRule]"
                                    ></v-text-field>
                                </template>
                                <template v-else>
                                    <p class="mb-4">
                                        De startdatum is doorgegeven: <strong>{{ formattedStartDate }}</strong>. Je kunt
                                        de verwachte einddatum hieronder invullen of aanpassen, zolang de evaluatie nog
                                        niet naar de aanvrager is verstuurd.
                                    </p>
                                </template>
                                <v-text-field outlined rounded hide-details="auto" class="mb-4"
                                    type="date"
                                    :label="isFirstSubmit ? 'Verwachte einddatum (optioneel)' : 'Verwachte einddatum'"
                                    v-model="expectedEndDate"
                                    :rules="isFirstSubmit ? [endAfterStartRule] : [requiredRule, endAfterStartRule]"
                                ></v-text-field>
                                <v-btn type="submit" color="primary" rounded block class="elevation-0" :loading="submitting" :disabled="submitting">
                                    {{ isFirstSubmit ? 'Datums doorgeven' : 'Einddatum opslaan' }}
                                </v-btn>
                            </v-form>
                            <v-alert v-else-if="!success" color="warning" class="mb-0">
                                De datums zijn al doorgegeven en kunnen niet meer gewijzigd worden.
                            </v-alert>
                        </v-card-text>
                    </v-card>
                </div>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import Headful from "@/components/layout/Headful";
import Header from "@/components/layout/Header";
import {formatDate} from "@/utils/formatters";

export default {
    name: "ProvideDates",
    components: { Headful, Header },
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        loading: true,
        notFound: false,
        submitting: false,
        success: false,
        successMessage: null,
        request: {},
        expectedStartDate: null,
        expectedEndDate: null,
        requiredRule: (value) => !!value || "Dit veld is verplicht.",
    }),
    computed: {
        isFirstSubmit() {
            return this.request.status === 'sent';
        },
        canSubmit() {
            return this.isFirstSubmit || this.request.endDateChangeable;
        },
        formattedStartDate() {
            return this.request.expectedStartDate ? formatDate(this.request.expectedStartDate) : null;
        },
        endAfterStartRule() {
            return (value) => !value || !this.expectedStartDate || value >= this.expectedStartDate
                || "De einddatum mag niet vóór de startdatum liggen.";
        },
    },
    mounted() {
        this.loadRequest()
            .catch(e => {
                console.error(e);
                this.notFound = true;
            })
            .finally(() => { this.loading = false; });
    },
    methods: {
        // Bij een eerder doorgegeven planning vullen we de bekende datums voor, zodat de
        // dienstverlener alleen de einddatum hoeft in te vullen of aan te passen.
        loadRequest() {
            return this.$store.dispatch('request/fetchDates', { token: this.token })
                .then(request => {
                    this.request = request;
                    this.expectedStartDate = request.expectedStartDate ? request.expectedStartDate.substring(0, 10) : null;
                    this.expectedEndDate = request.expectedEndDate ? request.expectedEndDate.substring(0, 10) : null;
                });
        },
        submit() {
            if (!this.$refs.form.validate() || this.submitting) {
                return;
            }

            this.submitting = true;
            const isFirstSubmit = this.isFirstSubmit;

            this.$store.dispatch('request/submitDates', {
                token: this.token,
                // Na het eerste doorgeven kan alleen de einddatum nog worden gewijzigd.
                expectedStartDate: isFirstSubmit ? this.expectedStartDate : null,
                expectedEndDate: this.expectedEndDate,
            })
                .then(() => {
                    this.successMessage = isFirstSubmit
                        ? 'Bedankt! De datums zijn doorgegeven. Via deze link kun je de einddatum later nog invullen of aanpassen.'
                        : 'Bedankt! De einddatum is opgeslagen.';
                    this.success = true;

                    // Na het eerste doorgeven wordt de einddatum verplicht; zonder reset kleurt
                    // het (nog lege) veld direct rood door de vorige validatie.
                    return this.loadRequest().then(() => this.$nextTick(() => {
                        if (this.$refs.form) this.$refs.form.resetValidation();
                    }));
                })
                .catch(e => {
                    console.error(e);
                    this.$store.dispatch('showSnackbar', { color: 'error', text: 'De datums konden niet worden opgeslagen' });
                })
                .finally(() => { this.submitting = false; });
        },
    },
}
</script>
